import React, { useEffect, useState, useCallback } from "react";
import "./CreateCohortModel.scss";
import { Modal } from "react-bootstrap";
import exportimages from "../../../../assets/images/exportImages";
import { useDispatch } from "react-redux";
import {
  createCohortSlice,
  setCohortApplied,
  setIsEditCohort,
  setIsFieldsEditiable,
  setMode,
  setSelectedCohort,
} from "../../../../rtk-query/features/cohort/cohortSlice";
import { usePermissions } from "../../../../hooks/usePermissions";
import { useSubscription } from "../../../../hooks/useSubscription";
import { PRIVILEGES } from "../../../../utils/privileges";
import { COHORT_MODE } from "../CohortList/CohortList";

/* -------------------- CONSTANTS -------------------- */
export const CREATE_MODEL = {
  CREATE: "create",
  SUCCESS: "success",
};

/* -------------------- HELPERS -------------------- */
const containsEmoji = (value = "") =>
  /[\p{Extended_Pictographic}]/u.test(value);

const validateField = (name, value) => {
  if (name === "cohortTitle") {
    if (!value) return "Cohort name is required";
    if (containsEmoji(value)) return "Emojis are not allowed";
  }

  if (name === "cohortDesc" && containsEmoji(value)) {
    return "Emojis are not allowed";
  }

  return "";
};

/* -------------------- FORM COMPONENT -------------------- */
const CreateCohortForm = ({
  form,
  errors,
  onChange,
  onSubmit,
  onCancel,
  isSubmitting,
  isAllowed,
}) => {
  return (
    <div className="create-cohort-modal">
      <div className="modal-header">
        <div className="icon-outer-circle">
          <div className="icon-inner-circle">
            <img alt="icon" src={exportimages.create_cohort_icon} />
          </div>
        </div>
      </div>

      <h2>Create Cohort</h2>
      <p className="subtitle">
        A bank cohort obtained by filtering is a group of banks selected based
        on demographics, asset size, and bank type for comparative analysis.
      </p>

      <div className="form-group">
        <label>Add Cohort As</label>
        <input
          type="text"
          name="cohortTitle"
          value={form.cohortTitle}
          placeholder="Enter cohort name"
          onChange={onChange}
          onBlur={onChange}
        />
        {errors.cohortTitle && (
          <span className="error-text">{errors.cohortTitle}</span>
        )}
      </div>

      <div className="form-group">
        <label>Description</label>
        <textarea
          name="cohortDesc"
          value={form.cohortDesc}
          placeholder="Enter cohort description"
          onChange={onChange}
        />
        {errors.cohortDesc && (
          <span className="error-text">{errors.cohortDesc}</span>
        )}
      </div>

      <div className="actions">
        <button className="btn cancel" onClick={onCancel}>
          Cancel
        </button>

        <button
          className="btn primary"
          onClick={onSubmit}
          disabled={isAllowed ? (isSubmitting ? true : false) : true}
        >
          {isSubmitting ? "Creating..." : "Create Cohort"}
        </button>
      </div>
    </div>
  );
};

/* -------------------- SUCCESS COMPONENT -------------------- */
const CreateCohortSuccess = ({ onClose, form }) => {
  return (
    <div className="success-cohort-modal">
      <div className="modal-header">
        <div className="icon-outer-circle">
          <div className="icon-inner-circle">
            <img alt="icon" src={exportimages.cohort_success_icon} />
          </div>
        </div>
      </div>

      <h2>Cohort Created Successfully</h2>
      <p className="subtitle">
        Cohort <strong>{form?.cohortTitle}</strong> was created successfully.
      </p>
      <div className="actions">
        <button className="btn cancel" onClick={onClose}>
          Close
        </button>
      </div>
    </div>
  );
};

/* -------------------- MAIN COMPONENT -------------------- */
export const CreateCohortModel = (props) => {
  const dispatch = useDispatch();
  const { hasPermission } = usePermissions();
  const { hasSubscription } = useSubscription();

  const [form, setForm] = useState({
    cohortTitle: "",
    cohortDesc: "",
  });

  const [errors, setErrors] = useState({
    cohortTitle: "",
    cohortDesc: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  /* -------------------- HANDLERS -------------------- */
  const handleChange = useCallback((e) => {
    const { name, value } = e.target;

    setForm((prev) => ({ ...prev, [name]: value }));

    const errorMsg = validateField(name, value);
    setErrors((prev) => ({ ...prev, [name]: errorMsg }));
  }, []);

  const isFormValid = form.cohortTitle && !errors.cohortTitle;

  const createCohort = async () => {
    if (!isFormValid) return;

    try {
      setIsSubmitting(true);

      const data = await dispatch(createCohortSlice(form)).unwrap();

      props.setSwitchCreateModel(CREATE_MODEL.SUCCESS);

      dispatch(setMode(COHORT_MODE.CUSTOM));
      dispatch(setCohortApplied(null));
      dispatch(setSelectedCohort(data?.data));
      dispatch(setIsFieldsEditiable(false));
      dispatch(setIsEditCohort(false));
    } catch (error) {
      console.error("Create cohort failed", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  /* -------------------- RESET -------------------- */
  useEffect(() => {
    if (props.switchCreateModel === CREATE_MODEL.CREATE) {
      setForm({ cohortTitle: "", cohortDesc: "" });
      setErrors({ cohortTitle: "", cohortDesc: "" });
      setIsSubmitting(false);
    }
  }, [props.switchCreateModel]);

  const isAllowed =
    hasPermission(PRIVILEGES.CREATE_COHORT) &&
    hasSubscription(PRIVILEGES.CREATE_COHORT) &&
    isFormValid;

  return (
    <Modal {...props} centered>
      <Modal.Body className="cohort-modal">
        {props.switchCreateModel === CREATE_MODEL.CREATE ? (
          <CreateCohortForm
            form={form}
            errors={errors}
            onChange={handleChange}
            onSubmit={createCohort}
            onCancel={props.onHide}
            isSubmitting={isSubmitting}
            isAllowed={isAllowed}
          />
        ) : (
          <CreateCohortSuccess onClose={props.onHide} form={form} />
        )}
      </Modal.Body>
    </Modal>
  );
};
